import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import StepForm from '../../../../../features/components/Forms/StepForm'
import LineItemStep from './Steps/LineItemStep'
import DateRangeStep from './Steps/DateRangeStep'
import PixelStep from './Steps/PixelStep'
import AudiencesStep from './Steps/AudiencesStep'
import GeolocationStep from './Steps/GeolocationStep'
import UrlsAndAppsStep from './Steps/UrlsAndAppsStep'
import CategoriesAndKeywordsStep from './Steps/CategoriesAndKeywordsStep'
import ExchangesStep from './Steps/ExchangesStep'
import LineItemTypeStep from './Steps/LineItemTypeStep'
import ViewabilityStep from './Steps/ViewabilityStep'
import PositionStep from './Steps/PositionStep'
import ThirdPartyVerificationStep from './Steps/ThirdPartyVerificationStep'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import { useLineItemDv360Create } from './useLineItemDv360Create'
import { getIsPixelRequired } from '../../../lineItemHelpers'

import {
  lineItemCreatedDataSelector,
  lineItemsSelector,
  lineItemWasCreatedSelector
} from '../../../../../modules/selectors/lineItems'
import { dv360RightUploadAdDataSelector } from '../../../../../modules/selectors/rights'
import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { clearLineItems } from '../../../../../modules/actions/lineItems'

import { LINE_ITEM } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/fields'
import { DV_360_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { LINE_ITEM_FORM } from '../index'

import { getInitialValues, getValidationSchema, LINE_ITEM_TYPE, VIDEO_DEFAULT } from './fields'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'

const LineItemForm = ({ onSuccessSubmit, formProgressItems }) => {
  const dispatch = useDispatch()

  const [isFirstLineItemCreation, setIsFirstLineItemCreation] = useState(false)
  const [manualSuccessSubmit, setManualSuccessSubmit] = useState(false)

  // line items are loaded when submitting previous CampaignForm with existing campaign option
  const lineItems = useSelector(lineItemsSelector)
  const lineItemDV360CreatedData = useSelector(lineItemCreatedDataSelector)
  const lineItemWasCreated = useSelector(lineItemWasCreatedSelector)

  const { campaign } = useSelector(dv360RightUploadAdDataSelector)

  const isLineItemsExist = !!lineItems.length

  const isPixelRequired = useMemo(() => {
    return getIsPixelRequired(campaign)
  }, [campaign])

  const {
    onSubmit: onLineItemCreateSubmit,
    onClearSubmit,
    isLoadingSelector,
    errorSelector,
    successSubmit
  } = useLineItemDv360Create()

  const onSubmit = useCallback(
    values => {
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedLineItemFullData = lineItems.find(lineItem => values[LINE_ITEM] === lineItem.id)

        dispatch(setRightUploadAdData({ lineItem: selectedLineItemFullData }, DV_360_PLATFORM))

        setManualSuccessSubmit(true)
      } else {
        // use double request line item creation from useLineItemGoogleCreate hook
        onLineItemCreateSubmit(values)
      }
    },
    [dispatch, lineItems, onLineItemCreateSubmit]
  )

  const formik = useFormik({
    initialValues: getInitialValues(isLineItemsExist),
    validationSchema: getValidationSchema(isPixelRequired),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values } = purifiedFormik

  const showOnlyLineItemStep = values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM
  const showLineItemStep = isLineItemsExist && !isFirstLineItemCreation

  const lineItemType = values[LINE_ITEM_TYPE]

  const steps = useMemo(
    () => [
      {
        component: <LineItemStep isOnlyLineItemStep={showOnlyLineItemStep} />,
        show: showLineItemStep
      },
      {
        component: <LineItemTypeStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <PixelStep />,
        show: !showOnlyLineItemStep && isPixelRequired
      },
      {
        component: <DateRangeStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <AudiencesStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <GeolocationStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <UrlsAndAppsStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <CategoriesAndKeywordsStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <ThirdPartyVerificationStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <ViewabilityStep />,
        show: !showOnlyLineItemStep
      },
      {
        component: <PositionStep />,
        show: !showOnlyLineItemStep && lineItemType === VIDEO_DEFAULT
      },
      {
        component: <ExchangesStep />,
        show: !showOnlyLineItemStep
      }
    ],
    [showOnlyLineItemStep, showLineItemStep, isPixelRequired, lineItemType]
  )

  // separate state to prevent bug, when LineItemStep becomes visible after first line item creation
  useEffect(() => {
    if (!lineItems.length) {
      setIsFirstLineItemCreation(true)
    }
  }, [lineItems.length, setIsFirstLineItemCreation])

  useEffect(() => {
    if (lineItemWasCreated) {
      dispatch(setRightUploadAdData({ lineItem: lineItemDV360CreatedData }, DV_360_PLATFORM))
    }
  }, [dispatch, lineItemWasCreated, lineItemDV360CreatedData])

  useEffect(
    () => () => {
      dispatch(clearLineItems())
    },
    [dispatch]
  )

  return (
    <StepForm
      steps={steps}
      formik={purifiedFormik}
      formName={LINE_ITEM_FORM}
      formProgressItems={formProgressItems}
      submitText="Save and continue"
      onSuccessSubmit={onSuccessSubmit}
      clearSubmitHandler={onClearSubmit}
      successSubmit={successSubmit || manualSuccessSubmit}
      errorSelector={errorSelector}
      isLoadingSelector={isLoadingSelector}
    />
  )
}

export default LineItemForm
